/**
 * Thin wrapper over the browser Notification API. Keeps permission checks
 * and cross-browser quirks in one place so components don't have to.
 */

const DISMISS_KEY = "tovra.desktopNotifs.dismissedAt";

export function notifSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notifPermission() {
  return notifSupported() ? Notification.permission : "unsupported";
}

/** Should we show the "enable desktop notifs" nudge banner? */
export function shouldPromptDesktopNotifs() {
  if (!notifSupported()) return false;
  if (Notification.permission !== "default") return false;
  const dismissed = localStorage.getItem(DISMISS_KEY);
  if (!dismissed) return true;
  // Re-nudge users after ~7 days if they dismissed but never granted.
  const ageMs = Date.now() - Number(dismissed);
  return Number.isFinite(ageMs) && ageMs > 7 * 24 * 60 * 60 * 1000;
}

export function dismissDesktopNotifsPrompt() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* localStorage blocked */ }
}

export async function requestDesktopNotifsPermission() {
  if (!notifSupported()) return "unsupported";
  try {
    // Some browsers still return a boolean here; normalise to string.
    const result = await Notification.requestPermission();
    return typeof result === "string" ? result : (result ? "granted" : "denied");
  } catch (_) {
    return "denied";
  }
}

/**
 * Fire a desktop notification. Silent no-op if not granted or unsupported.
 * Returns the Notification instance (or null) so callers can attach onclick
 * or auto-close timers.
 */
export function fireDesktopNotif(title, { body, tag, icon, onClick, silent = false } = {}) {
  if (!notifSupported() || Notification.permission !== "granted") return null;
  // Don't spam the OS notification tray when the user is already looking at
  // the tab — the in-app banner is enough.
  if (typeof document !== "undefined" && document.visibilityState === "visible" && document.hasFocus()) {
    return null;
  }
  try {
    const n = new Notification(title, {
      body,
      tag,     // dedupes duplicates for the same call id
      icon: icon || "/favicon.ico",
      silent,  // let our WebAudio chime own the sound
    });
    if (onClick) {
      n.onclick = (e) => {
        e.preventDefault();
        try { window.focus(); } catch (_) { /* focus blocked */ }
        onClick();
        try { n.close(); } catch (_) { /* close blocked */ }
      };
    }
    // Auto-close after 12s so unanswered notifications don't linger forever.
    setTimeout(() => { try { n.close(); } catch (_) { /* close blocked */ } }, 12000);
    return n;
  } catch (_) {
    return null;
  }
}
